<template>
  <div
    class="conversation-details-wrap bg-slate-25 dark:bg-slate-800"
    :class="{ 'with-border-right': !isOnExpandedLayout }"
  >
    <conversation-header
      v-if="currentChat.id && displayHeaderInMobile"
      :chat="currentChat"
      :is-contact-panel-open="isContactPanelOpen"
      :show-back-button="isOnExpandedLayout"
      @contact-panel-toggle="onToggleContactPanel"
    />
    <woot-tabs
      v-if="dashboardApps.length && currentChat.id"
      :index="activeIndex"
      class="dashboard-app--tabs bg-white dark:bg-slate-900 -mt-px"
      @change="onDashboardAppTabChange"
    >
      <woot-tabs-item
        v-for="tab in dashboardAppTabs"
        :key="tab.key"
        :name="tab.name"
        :show-badge="false"
      />
    </woot-tabs>
    <div
      v-show="!activeIndex"
      class="flex bg-slate-25 dark:bg-slate-800 m-0 h-full min-h-0"
    >
      <messages-view
        v-if="currentChat.id"
        :inbox-id="inboxId"
        :is-contact-panel-open="isContactPanelOpen"
        @contact-panel-toggle="onToggleContactPanel"
      />
      <empty-state v-else :is-on-expanded-layout="isOnExpandedLayout" />
      <div
        v-show="showContactPanel"
        class="conversation-sidebar-wrap basis-full sm:basis-[17.5rem] md:basis-[18.75rem] lg:basis-[19.375rem] xl:basis-[20.625rem] 2xl:basis-[25rem] rtl:border-r border-slate-50 dark:border-slate-700 h-auto overflow-auto z-10 flex-shrink-0 flex-grow-0"
        :class="{ 'is-resizable': resizableSidebar, 'is-resizing': isSidebarResizing }"
        :style="sidebarStyle"
      >
        <div
          v-if="resizableSidebar && !isSmartphone"
          class="resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          tabindex="0"
          @mousedown="onSidebarResizeStart"
          @keydown.prevent="onSidebarHandleKeydown"
        />
        <contact-panel
          v-if="showContactPanel"
          :conversation-id="currentChat.id"
          :inbox-id="currentChat.inbox_id"
          :on-toggle="onToggleContactPanel"
        />
      </div>
    </div>
    <dashboard-app-frame
      v-for="(dashboardApp, index) in dashboardApps"
      v-show="activeIndex - 1 === index"
      :key="currentChat.id + '-' + dashboardApp.id"
      :is-visible="activeIndex - 1 === index"
      :config="dashboardApps[index].content"
      :current-chat="currentChat"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel';
import deviceMixins from 'shared/mixins/deviceMixins';

import ConversationHeader from './ConversationHeader';
import DashboardAppFrame from '../DashboardApp/Frame.vue';
import EmptyState from './EmptyState/EmptyState';
import MessagesView from './MessagesView';


export default {
  components: {
    ContactPanel,
    ConversationHeader,
    DashboardAppFrame,
    EmptyState,
    MessagesView,
  },

  mixins: [deviceMixins],

  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      activeIndex: 0,
      // Sidebar resize state
      resizableSidebar: true,
      sidebarWidth: null,
      isSidebarResizing: false,
      startX: 0,
      startSidebarWidth: 0,
      minSidebarWidth: 280,
      maxSidebarWidth: 640,
      sidebarWidthStorageKey: 'conversationSidebarWidth',
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      dashboardApps: 'dashboardApps/getRecords',
    }),
    dashboardAppTabs() {
      return [
        {
          key: 'messages',
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
        },
        ...this.dashboardApps.map(dashboardApp => ({
          key: `dashboard-${dashboardApp.id}`,
          name: dashboardApp.title,
        })),
      ];
    },
    showContactPanel() {
      return this.isContactPanelOpen && this.currentChat.id;
    },
    displayHeaderInMobile() {
      if (this.isSmartphone && this.showContactPanel) {
        return false
      }
      return true
    },
    sidebarStyle() {
      if (!this.resizableSidebar || !this.sidebarWidth) return {};
      return {
        width: `${this.sidebarWidth}px`,
        flex: '0 0 auto',
        flexBasis: `${this.sidebarWidth}px`,
      };
    },
  },
  watch: {
    'currentChat.inbox_id'(inboxId) {
      if (inboxId) {
        this.$store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
      }
    },
    'currentChat.id'() {
      this.fetchLabels();
      this.activeIndex = 0;
    },
  },
  mounted() {
    this.fetchLabels();
    this.$store.dispatch('dashboardApps/get');
    // Initialize sidebar width
    if (this.resizableSidebar) {
      try {
        const saved = localStorage.getItem(this.sidebarWidthStorageKey);
        if (saved) {
          const w = parseInt(saved, 10);
          if (Number.isFinite(w)) {
            this.sidebarWidth = Math.max(
              this.minSidebarWidth,
              Math.min(this.maxSidebarWidth, w)
            );
          }
        }
      } catch (e) {
        // ignore storage errors
      }
    }
  },
  methods: {
    fetchLabels() {
      if (!this.currentChat.id) {
        return;
      }
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    onToggleContactPanel() {
      this.$emit('contact-panel-toggle');
    },
    onDashboardAppTabChange(index) {
      this.activeIndex = index;
    },
    onSidebarResizeStart(e) {
      if (!this.resizableSidebar) return;
      this.isSidebarResizing = true;
      this.startX = e.clientX;
      // Measure current width of the sidebar element
      this.$nextTick(() => {
        const el = this.$el.querySelector('.conversation-sidebar-wrap');
        if (el) {
          this.startSidebarWidth = el.getBoundingClientRect().width;
          if (!this.sidebarWidth) {
            this.sidebarWidth = Math.round(this.startSidebarWidth);
          }
        } else {
          this.startSidebarWidth = this.sidebarWidth || this.minSidebarWidth;
        }
      });
      window.addEventListener('mousemove', this.onSidebarResizeMove);
      window.addEventListener('mouseup', this.onSidebarResizeEnd);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    },
    onSidebarResizeMove(e) {
      if (!this.isSidebarResizing) return;
      const delta = e.clientX - this.startX; // handle on left edge
      const next = this.startSidebarWidth - delta; // drag left increases width
      const clamped = Math.max(
        this.minSidebarWidth,
        Math.min(this.maxSidebarWidth, next)
      );
      this.sidebarWidth = Math.round(clamped);
    },
    onSidebarResizeEnd() {
      if (!this.isSidebarResizing) return;
      this.isSidebarResizing = false;
      window.removeEventListener('mousemove', this.onSidebarResizeMove);
      window.removeEventListener('mouseup', this.onSidebarResizeEnd);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      try {
        if (this.sidebarWidth) {
          localStorage.setItem(
            this.sidebarWidthStorageKey,
            String(this.sidebarWidth)
          );
        }
      } catch (e) {
        // ignore
      }
    },
    onSidebarHandleKeydown(e) {
      if (!this.resizableSidebar) return;
      const step = e.shiftKey ? 20 : 10;
      if (e.key === 'ArrowLeft') {
        // ArrowLeft expands sidebar (handle on left edge)
        this.sidebarWidth = Math.min(
          this.maxSidebarWidth,
          (this.sidebarWidth || this.minSidebarWidth) + step
        );
        this.persistSidebarWidth();
      } else if (e.key === 'ArrowRight') {
        // ArrowRight shrinks sidebar
        this.sidebarWidth = Math.max(
          this.minSidebarWidth,
          (this.sidebarWidth || this.minSidebarWidth) - step
        );
        this.persistSidebarWidth();
      }
    },
    persistSidebarWidth() {
      try {
        if (this.sidebarWidth) {
          localStorage.setItem(
            this.sidebarWidthStorageKey,
            String(this.sidebarWidth)
          );
        }
      } catch (e) {
        // ignore
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.conversation-details-wrap {
  @apply flex flex-col min-w-0 w-full;

  &.with-border-right {
    @apply border-r border-slate-50 dark:border-slate-700;
  }
}

.dashboard-app--tabs {
  ::v-deep {
    .tabs-title {
      a {
        @apply pb-2 pt-1;
      }
    }
  }
}

.conversation-sidebar-wrap {
  position: relative;
  /* Ensure width is honored when resizable */
  &.is-resizable {
    flex: 0 0 auto;
  }

  .resize-handle {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0; /* handle lives on the left edge */
    width: 6px;
    cursor: col-resize;
    z-index: 20;
    background: transparent;
  }

  .resize-handle:hover,
  &.is-resizing .resize-handle {
    background: rgba(125, 125, 125, 0.15);
  }

  &::v-deep .contact--panel {
    @apply w-full h-full max-w-full;
  }
}
</style>
